import React from "react";
import { motion } from "framer-motion";
import { Calendar } from "./ui/calendar";
import { TIME_SLOTS } from "../data/content";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduleVisit({ hideHeader = false }) {
  const [date, setDate] = React.useState(null);
  const [slot, setSlot] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(null);
  const [form, setForm] = React.useState({
    parent_name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const disabled = (d) => {
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    // Disable past days and Sundays
    if (day < today) return true;
    if (day.getDay() === 0) return true;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please select a date for your visit.");
      return;
    }
    if (!slot) {
      toast.error("Please pick a time slot.");
      return;
    }
    if (!form.parent_name || !form.email || !form.phone) {
      toast.error("Please complete your contact details.");
      return;
    }
    setSubmitting(true);
    try {
      const visit_date = date.toISOString().slice(0, 10);
      const res = await axios.post(`${API}/visits`, {
        ...form,
        visit_date,
        time_slot: slot,
      });
      setConfirmed(res.data);
      setForm({ parent_name: "", email: "", phone: "", notes: "" });
      setDate(null);
      setSlot("");
      toast.success("Your campus visit is confirmed. Check your email for details.");
    } catch (err) {
      toast.error("We couldn't confirm your visit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="schedule-visit"
      className="relative py-14 md:py-28 bg-[#DCEFE1] overflow-hidden"
      data-testid="schedule-visit-section"
    >
      <div
        className="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-15"
        style={{ background: "#648DE5" }}
      />
      <div
        className="absolute bottom-10 right-10 shape-square opacity-20"
        style={{ width: 140, height: 140, background: "#C6D947" }}
      />

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        {!hideHeader && (
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-5 block">
                Visit the Campus
              </span>
              <h2 className="font-display text-[#0F1B47] text-[32px] md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold">
                See it for yourself.
                <br />
                <span className="italic font-normal">Take the tour</span>.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#4A5568] text-base leading-relaxed font-light">
                Private, guided walks through our studios, gardens and classrooms —
                weekdays & Saturdays. Your child is welcome to join.
              </p>
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="grid lg:grid-cols-12 gap-5"
          data-testid="visit-form"
        >
          {/* Calendar */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 bg-[#FDFDF9] rounded-3xl p-6 md:p-8 border border-[#0F1B47]/6"
          >
            <div className="flex items-center gap-2 mb-5 text-[#0F1B47]">
              <CalendarDays size={18} />
              <span className="text-xs font-bold tracking-[0.18em] uppercase">
                Pick a date
              </span>
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={disabled}
              className="mx-auto"
              data-testid="visit-calendar"
            />
            {date && (
              <div className="mt-4 text-center text-sm text-[#4A5568]" data-testid="visit-selected-date">
                Selected: <span className="text-[#0F1B47] font-semibold">{date.toDateString()}</span>
              </div>
            )}
          </motion.div>

          {/* Form fields + slots */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 bg-[#FDFDF9] rounded-3xl p-8 md:p-10 border border-[#0F1B47]/6"
          >
            {confirmed && (
              <div
                className="mb-6 flex items-start gap-3 bg-[#5BC4B8]/15 border border-[#5BC4B8]/40 rounded-2xl px-5 py-4 text-[#0F1B47]"
                data-testid="visit-success"
              >
                <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-sm">Visit confirmed</div>
                  <div className="text-sm mt-1 font-light">
                    {confirmed.visit_date} at {confirmed.time_slot}. We've saved your slot — see you then.
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-5 text-[#0F1B47]">
              <Clock size={18} />
              <span className="text-xs font-bold tracking-[0.18em] uppercase">
                Choose a time slot
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2.5 mb-8">
              {TIME_SLOTS.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSlot(s)}
                  className={`rounded-2xl px-3 py-3 text-sm font-semibold transition-all border ${
                    slot === s
                      ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                      : "bg-[#FDFDF9] text-[#0F1B47] border-[#0F1B47]/15 hover:border-[#648DE5] hover:-translate-y-0.5"
                  }`}
                  data-testid={`visit-slot-${s.replace(/[\s:]/g, "")}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Parent's name"
                value={form.parent_name}
                onChange={(v) => setForm({ ...form, parent_name: v })}
                placeholder="Ananya Mehta"
                testid="visit-parent-name"
              />
              <Input
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                placeholder="you@email.com"
                testid="visit-email"
              />
              <Input
                label="Phone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                placeholder="+91 98 000 00000"
                testid="visit-phone"
              />
              <Input
                label="Notes (optional)"
                value={form.notes}
                onChange={(v) => setForm({ ...form, notes: v })}
                placeholder="Child's age, interests…"
                testid="visit-notes"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-[#4A5568] text-sm">
                <MapPin size={16} />
                <span>45 mins · on-campus · guided</span>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] hover:shadow-xl disabled:opacity-60"
                data-testid="visit-submit"
              >
                {submitting ? "Confirming…" : "Confirm my visit"}
              </button>
            </div>
          </motion.div>
        </form>
      </div>
    </section>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all"
      />
    </label>
  );
}
